import { PropsWithChildren, Suspense, useEffect, useLayoutEffect, useRef } from "react";
import { Navigate, RouteObject, useLocation } from "react-router-dom";
import { Box } from "@mui/material";
import { QueryParamProvider } from "use-query-params";
import { ReactRouter6Adapter } from "use-query-params/adapters/react-router-6";
import yn from "yn";
import AppHeader, { HEADER_HEIGHT } from "~/components/AppHeader";
import Loading from "~/components/Loading";
import { ManifoldNextGenLayout } from "~/components/ManifoldNextGenLayout";
import useAppHeader from "~/contexts/header";
import { useForceManifoldNextGenFeature } from "~/hooks/useManifoldNextGenFeature";
import { useUserPreferences } from "~/hooks/useUserPreferences";
import { NEXTGEN_MAX_CONTENT_WIDTH } from "~/lib/layout";
import {
  Admin,
  Cohorts,
  DataBrowser,
  DataOverview,
  Home,
  NotFound,
  ResearchWorkbench,
  Shared,
  SmPortal,
  ShadcnTest,
} from "~/pages";
import Groups from "~/pages/admin/components/user-management/Groups";
import Users from "~/pages/admin/components/user-management/Users";
import { AuditLog } from "~/pages/datasets/components/AuditLog";
import { AgentConnectorsTab } from "~/pages/manifold-nextgen/components/agents/AgentConnectorsTab";
import { AgentConversationsTab } from "~/pages/manifold-nextgen/components/agents/AgentConversationsTab";
import { AgentDetail } from "~/pages/manifold-nextgen/components/agents/AgentDetail";
import { AgentFeaturedConversationsTab } from "~/pages/manifold-nextgen/components/agents/AgentFeaturedConversationsTab";
import { AgentProfileTab } from "~/pages/manifold-nextgen/components/agents/AgentProfileTab";
import { AgentSettings } from "~/pages/manifold-nextgen/components/agents/AgentSettings";
import { AgentSkillsTab } from "~/pages/manifold-nextgen/components/agents/AgentSkillsTab";
import { AgentStarterPromptsTab } from "~/pages/manifold-nextgen/components/agents/AgentStarterPromptsTab";
import { AgentToolkitsTab } from "~/pages/manifold-nextgen/components/agents/AgentToolkitsTab";
import { Agents } from "~/pages/manifold-nextgen/components/agents/Agents";
import { AdminConversationRoute } from "~/pages/manifold-nextgen/components/conversations/AdminConversationRoute";
import { ConversationRoute } from "~/pages/manifold-nextgen/components/conversations/ConversationRoute";
import { SharedConversationRoute } from "~/pages/manifold-nextgen/components/conversations/SharedConversationRoute";
import { DatasetDetail } from "~/pages/manifold-nextgen/components/datasets/DatasetDetail";
import { DatasetSettings } from "~/pages/manifold-nextgen/components/datasets/DatasetSettings";
import { Datasets } from "~/pages/manifold-nextgen/components/datasets/Datasets";
import { DatasetAIContext } from "~/pages/manifold-nextgen/components/datasets/pages/DatasetAIContext";
import { DatasetAISkillsTab } from "~/pages/manifold-nextgen/components/datasets/pages/DatasetAISkillsTab";
import { DatasetAccessRequests } from "~/pages/manifold-nextgen/components/datasets/pages/DatasetAccessRequests";
import { DatasetAddTable } from "~/pages/manifold-nextgen/components/datasets/pages/DatasetAddTable";
import { DatasetCohortExplorer } from "~/pages/manifold-nextgen/components/datasets/pages/DatasetCohortExplorer";
import { DatasetDashboards } from "~/pages/manifold-nextgen/components/datasets/pages/DatasetDashboards";
import { DatasetFiles } from "~/pages/manifold-nextgen/components/datasets/pages/DatasetFiles";
import { DatasetOverview } from "~/pages/manifold-nextgen/components/datasets/pages/DatasetOverview";
import { DatasetReport } from "~/pages/manifold-nextgen/components/datasets/pages/DatasetReport";
import { DatasetTableDetail } from "~/pages/manifold-nextgen/components/datasets/pages/DatasetTableDetail";
import { DatasetTables } from "~/pages/manifold-nextgen/components/datasets/pages/DatasetTables";
import { ProjectIndex } from "~/pages/manifold-nextgen/components/projects/ProjectIndex";
import { ProjectLayout } from "~/pages/manifold-nextgen/components/projects/ProjectLayout";
import { ProjectOverview } from "~/pages/manifold-nextgen/components/projects/ProjectOverview";
import { ProjectSettings } from "~/pages/manifold-nextgen/components/projects/ProjectSettings";
import { Projects } from "~/pages/manifold-nextgen/components/projects/Projects";
import { ROUTE_NEXTGEN_VIEW, ROUTE_SHARED } from "~/router/constants";
import { ProtectedAppRoute } from "./components/ProtectedAppRoute";
import redirects from "./redirects";
import { ROUTE_404 } from "./router/constants";
import { VIEW_ADMIN_APP, VIEW_ALL_AI_CONVERSATIONS, VIEW_SM_APP } from "./router/permissions";

interface AppLayoutProps extends PropsWithChildren {
  enabled?: boolean;
}

const {
  VITE_RWB_ENABLED,
  VITE_SM_ENABLED,
  VITE_OUTREACH_API,
  VITE_PARTICIPANT_API,
  VITE_SURVEY_API,
  VITE_DATA_DASHBOARD_URL,
  VITE_ADMIN_APP_ENABLED,
  VITE_COHORT_EXPLORER_ENABLED,
} = import.meta.env;

/**
 * Top-level app layout
 * @param enabled - Build-time, env specific feature flag support
 * @param children
 * @constructor
 */
const AppLayout = ({ enabled = true, children }: AppLayoutProps) => {
  const location = useLocation();
  const wrapperRef = useRef<HTMLElement>(null);
  const { setCenterContent } = useAppHeader();
  const contentHeight = `calc(100vh - ${HEADER_HEIGHT})`;
  const [preferences] = useUserPreferences();
  const { enabled: forceManifoldNextGen } = useForceManifoldNextGenFeature();

  useEffect(() => {
    // Handle resetting scroll on navigation
    wrapperRef.current?.scrollTo({ top: 0 });
  }, [wrapperRef.current, location.pathname]);

  useLayoutEffect(() => {
    // Ugly hack to avoid flicker between tab switches on participant profile
    // TODO: Deprecate in favor of a more generic solution for multi-page center content
    if (!/^\/sm\/participants\/\d+.*$/.test(location.pathname)) {
      // Reset page specific center content when navigating
      setCenterContent(null);
    }
  }, [location.pathname, setCenterContent]);

  if (!enabled) {
    return <Navigate to={ROUTE_404} replace />;
  }

  if (
    (preferences.optedIntoManifoldNextGen || forceManifoldNextGen) &&
    !location.pathname.startsWith(ROUTE_NEXTGEN_VIEW) &&
    // Exempt /shared/* so row clicks from ConversationTable land on the classic conversation viewer
    !location.pathname.startsWith(ROUTE_SHARED)
  ) {
    return <Navigate to={ROUTE_NEXTGEN_VIEW} replace />;
  }

  return (
    <QueryParamProvider adapter={ReactRouter6Adapter} options={{ removeDefaultsFromUrl: true }}>
      <AppHeader />
      <Box
        component="main"
        ref={wrapperRef}
        sx={{
          position: "relative",
          height: contentHeight,
          overflow: "auto",
          bgcolor: "common.pageBackground",
        }}
      >
        {children}
      </Box>
    </QueryParamProvider>
  );
};

/**
 * Wrapper for lazy imported apps
 * @param children - Route content which might also include its own sub-router
 * @constructor
 */
const LazyWrapper = ({ children }: PropsWithChildren) => (
  <Suspense fallback={<Loading />}>{children}</Suspense>
);

const routes: RouteObject[] = [
  {
    index: true,
    element: (
      <AppLayout>
        <Home />
      </AppLayout>
    ),
  },
  ...redirects,
  {
    path: "shadcn-test",
    element: (
      <AppLayout enabled={import.meta.env.DEV}>
        <ShadcnTest />
      </AppLayout>
    ),
  },
  // Note: Global files browser is now under /data-browser/files
  // The redirect from /files to /data-browser/files is in redirects.tsx
  {
    path: "cohorts/*",
    element: (
      <AppLayout enabled={yn(VITE_COHORT_EXPLORER_ENABLED, { default: false })}>
        <LazyWrapper>
          <Cohorts />
        </LazyWrapper>
      </AppLayout>
    ),
  },
  {
    path: "workbenches/*",
    element: (
      <AppLayout enabled={yn(VITE_RWB_ENABLED, { default: false })}>
        <LazyWrapper>
          <ResearchWorkbench />
        </LazyWrapper>
      </AppLayout>
    ),
  },
  {
    path: "data-browser/*",
    element: (
      <AppLayout>
        <DataBrowser />
      </AppLayout>
    ),
  },
  {
    path: "data-overview",
    element: (
      <AppLayout enabled={!!VITE_DATA_DASHBOARD_URL}>
        <DataOverview />
      </AppLayout>
    ),
  },
  {
    path: "sm/*",
    element: (
      <AppLayout enabled={yn(VITE_SM_ENABLED, { default: false })}>
        <LazyWrapper>
          <ProtectedAppRoute permission={VIEW_SM_APP}>
            <SmPortal />
          </ProtectedAppRoute>
        </LazyWrapper>
      </AppLayout>
    ),
    handle: {
      serverHealth: [
        {
          endpoint: VITE_OUTREACH_API,
          label: "Outreach Service",
        },
        {
          endpoint: VITE_PARTICIPANT_API,
          label: "Participant Service",
        },
        {
          endpoint: VITE_SURVEY_API,
          label: "Survey Service",
        },
      ],
    },
  },
  {
    path: "admin/*",
    element: (
      <AppLayout enabled={yn(VITE_ADMIN_APP_ENABLED, { default: false })}>
        <LazyWrapper>
          <Admin />
        </LazyWrapper>
      </AppLayout>
    ),
  },
  {
    path: "shared/*",
    element: (
      <AppLayout>
        <LazyWrapper>
          <Shared />
        </LazyWrapper>
      </AppLayout>
    ),
  },
  {
    path: "nextgen",
    element: <ManifoldNextGenLayout />,
    children: [
      {
        index: true,
        element: <Navigate to="/nextgen/projects" replace />,
      },
      {
        path: "agents",
        element: <Agents />,
      },
      {
        path: "datasets",
        element: <Datasets />,
      },
      {
        path: "datasets/:datasetId",
        element: <DatasetDetail />,
        children: [
          { index: true, element: <Navigate to="overview" replace /> },
          { path: "overview", element: <DatasetOverview /> },
          { path: "dashboards", element: <DatasetDashboards /> },
          { path: "dashboards/:reportId", element: <DatasetReport /> },
          {
            path: "tables",
            children: [
              { index: true, element: <DatasetTables /> },
              { path: "new", element: <DatasetAddTable /> },
              { path: ":tableFQN", element: <DatasetTableDetail /> },
            ],
          },
          { path: "files", element: <DatasetFiles /> },
          { path: "files/:prefixId/*", element: <DatasetFiles /> },
          { path: "ai-context", element: <DatasetAIContext /> },
          { path: "ai-skills", element: <DatasetAISkillsTab /> },
          { path: "cohort-explorer", element: <DatasetCohortExplorer /> },
          { path: "access-requests", element: <DatasetAccessRequests /> },
          { path: "audit-log", element: <AuditLog /> },
          { path: "settings", element: <DatasetSettings /> },
        ],
      },
      {
        path: "agents/:agentId",
        element: <AgentDetail />,
        children: [
          {
            index: true,
            element: <Navigate to="profile" replace />,
          },
          {
            path: "profile",
            element: <AgentProfileTab />,
          },
          {
            path: "skills",
            element: <AgentSkillsTab />,
          },
          {
            path: "toolkits",
            element: <AgentToolkitsTab />,
          },
          {
            path: "connectors",
            element: <AgentConnectorsTab />,
          },
          {
            path: "featured-conversations",
            element: <AgentFeaturedConversationsTab />,
          },
          {
            path: "starter-prompts",
            element: <AgentStarterPromptsTab />,
          },
          {
            path: "sessions",
            element: <AgentConversationsTab />,
          },
          {
            path: "settings",
            element: <AgentSettings />,
          },
        ],
      },
      {
        path: "projects",
        element: <Projects />,
      },
      {
        path: "projects/:projectId",
        element: <ProjectLayout />,
        children: [
          { index: true, element: <ProjectOverview /> },
          { path: "chats", element: <ProjectIndex /> },
          { path: "settings", element: <ProjectSettings /> },
          { path: "c/:conversationId", element: <ConversationRoute /> },
        ],
      },
      {
        path: "shared/c/:token",
        element: <SharedConversationRoute />,
      },
      {
        path: "admin/users",
        element: (
          <ProtectedAppRoute permission={VIEW_ADMIN_APP}>
            <Users maxContentWidth={NEXTGEN_MAX_CONTENT_WIDTH} />
          </ProtectedAppRoute>
        ),
      },
      {
        path: "admin/groups",
        element: (
          <ProtectedAppRoute permission={VIEW_ADMIN_APP}>
            <Groups maxContentWidth={NEXTGEN_MAX_CONTENT_WIDTH} />
          </ProtectedAppRoute>
        ),
      },
      {
        path: "admin/c/:conversationId",
        element: (
          <ProtectedAppRoute permission={VIEW_ALL_AI_CONVERSATIONS}>
            <AdminConversationRoute />
          </ProtectedAppRoute>
        ),
      },
    ],
  },
  { path: "404", element: <NotFound /> },
  { path: "*", element: <Navigate to={ROUTE_404} replace /> },
];

export default routes;
